import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Container, Form, Spinner, Table } from "react-bootstrap";
import { FaPaperclip } from "react-icons/fa";
import { EmailFilingUploadResult, fileMessages, getMailMessages, MailMessageSummary, PinDestinationRequest } from "@/api/emailFilingApi";
import SharePointDestinationPicker from "@/components/EmailFiling/SharePointDestinationPicker";

const EmailFilingHome: React.FC = () => {
  const [messages, setMessages] = useState<MailMessageSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filingError, setFilingError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showPicker, setShowPicker] = useState(false);
  const [chosenDestination, setChosenDestination] = useState<PinDestinationRequest | null>(null);
  const [filingInProgress, setFilingInProgress] = useState(false);
  const [filingResults, setFilingResults] = useState<EmailFilingUploadResult[]>([]);
  const [renameMap, setRenameMap] = useState<Record<string, string>>({});
  const [retentionLabel, setRetentionLabel] = useState("");

  const suggestedFileName = (message: MailMessageSummary) => {
    const subject = (message.subject || "Email").trim() || "Email";
    const safeSubject = subject.replace(/[<>:"/\\|?*]+/g, "-").replace(/\s+/g, " ").trim();
    const sentAt = message.receivedDateTime ? new Date(message.receivedDateTime) : new Date();
    const stamp = sentAt.toISOString().slice(0, 10).replace(/-/g, "");
    return `${safeSubject || "Email"}_${stamp}.eml`;
  };

  useEffect(() => {
    if (selectedIds.size === 0) {
      setRenameMap({});
      return;
    }

    setRenameMap((prev) => {
      const next = { ...prev };
      for (const message of messages) {
        if (!selectedIds.has(message.messageId)) continue;
        next[message.messageId] ??= suggestedFileName(message);
      }
      for (const id of Object.keys(next)) {
        if (!selectedIds.has(id)) delete next[id];
      }
      return next;
    });
  }, [messages, selectedIds]);

  const load = async (searchTerm?: string) => {
    setLoading(true);
    setError(null);
    try {
      setMessages(await getMailMessages(searchTerm));
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || "Failed to load mail messages.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    void load(search);
  };

  const toggleSelected = (messageId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) next.delete(messageId); else next.add(messageId);
      return next;
    });
  };

  const handleFileSelectedEmails = async () => {
    if (!chosenDestination || selectedIds.size === 0) {
      return;
    }

    setFilingInProgress(true);
    setFilingError(null);
    try {
      const results = await fileMessages({
        messageIds: Array.from(selectedIds),
        destination: chosenDestination,
        fileNameOverrides: Object.fromEntries(
          Array.from(selectedIds).map((messageId) => [messageId, renameMap[messageId] || suggestedFileName(messages.find((message) => message.messageId === messageId) || { messageId, subject: "Email", receivedDateTime: undefined, senderName: "", senderEmail: "", hasAttachments: false, recipientNames: [] })])
        ),
        retentionLabel: retentionLabel.trim() || undefined,
      });

      setFilingResults(results);
      const failed = results.filter((result) => !result.succeeded);
      if (failed.length > 0) {
        setFilingError(`${failed.length} of ${results.length} emails did not file successfully.`);
      }
      setSelectedIds(new Set());
      setChosenDestination(null);
    } catch (err: any) {
      setFilingError(err?.response?.data?.error || err?.message || "Failed to file the selected emails.");
    } finally {
      setFilingInProgress(false);
    }
  };

  return (
    <Container fluid className="px-4 py-3">
      <h4>Email Filing</h4>
      <p className="text-muted">Select emails from your inbox to file into SharePoint.</p>

      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {filingError && <Alert variant="warning" onClose={() => setFilingError(null)} dismissible>{filingError}</Alert>}

      {filingResults.length > 0 && (
        <Alert variant={filingResults.some((result) => !result.succeeded) ? "warning" : "success"} className="mt-2">
          <div className="fw-semibold mb-1">Filing results</div>
          <ul className="mb-0 ps-3">
            {filingResults.map((result) => (
              <li key={result.messageId}>
                {result.succeeded ? (
                  <>
                    {result.fileName} filed successfully{result.sharePointWebUrl ? ` — ${result.sharePointWebUrl}` : ""}
                  </>
                ) : (
                  <>
                    {result.fileName || result.messageId}: {result.error || "Upload failed"}
                  </>
                )}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      <Form className="d-flex gap-2 mb-3" onSubmit={handleSearchSubmit}>
        <Form.Control type="search" placeholder="Search email" value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 320 }} />
        <Button type="submit" variant="outline-secondary">Search</Button>
      </Form>

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : messages.length === 0 ? (
            <div className="text-muted text-center py-4">No messages found.</div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead><tr><th></th><th>Subject</th><th>Sender</th><th>Received</th><th></th></tr></thead>
              <tbody>
                {messages.map((message) => (
                  <tr key={message.messageId}>
                    <td><Form.Check type="checkbox" checked={selectedIds.has(message.messageId)} onChange={() => toggleSelected(message.messageId)} /></td>
                    <td className="fw-semibold">{message.subject}</td>
                    <td className="small text-muted">{message.senderName}</td>
                    <td className="small">{message.receivedDateTime ? new Date(message.receivedDateTime).toLocaleString() : "-"}</td>
                    <td>{message.hasAttachments && <Badge bg="secondary"><FaPaperclip /></Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {selectedIds.size > 0 && (
        <Card className="mt-3">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="fw-semibold">Review file names</div>
              <small className="text-muted">{selectedIds.size} selected</small>
            </div>
            {Array.from(selectedIds).map((messageId) => {
              const message = messages.find((item) => item.messageId === messageId);
              if (!message) return null;

              return (
                <Form.Group key={messageId} className="mb-2">
                  <Form.Label className="small text-muted">{message.subject}</Form.Label>
                  <Form.Control
                    type="text"
                    value={renameMap[messageId] ?? suggestedFileName(message)}
                    onChange={(e) => setRenameMap((prev) => ({ ...prev, [messageId]: e.target.value }))}
                  />
                </Form.Group>
              );
            })}
            <Form.Group className="mt-3">
              <Form.Label className="small text-muted">Retention label (optional)</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Finance - 7 years"
                value={retentionLabel}
                onChange={(e) => setRetentionLabel(e.target.value)}
              />
            </Form.Group>
          </Card.Body>
        </Card>
      )}

      <div className="mt-3 d-flex justify-content-end align-items-center gap-2">
        {chosenDestination && (
          <span className="small text-muted">Destination: {chosenDestination.siteName} / {chosenDestination.folderName}</span>
        )}
        <Button
          variant="primary"
          disabled={selectedIds.size === 0 || filingInProgress}
          onClick={() => {
            if (!chosenDestination) {
              setShowPicker(true);
              return;
            }
            void handleFileSelectedEmails();
          }}
        >
          {filingInProgress ? <><Spinner animation="border" size="sm" className="me-2" /> Filing...</> : !chosenDestination ? `Choose destination (${selectedIds.size})` : `File ${selectedIds.size} Email${selectedIds.size === 1 ? "" : "s"}`}
        </Button>
      </div>

      <SharePointDestinationPicker
        show={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={(destination) => {
          setChosenDestination(destination);
          setShowPicker(false);
        }}
      />
    </Container>
  );
};

export default EmailFilingHome;
